// ============================================================
// FILE: src/pages/Login/LoginPage.jsx
// ============================================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../api/services';
import './LoginPage.css';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [form, setForm]         = useState({ username: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const handleChange = (e) =>
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.username || !form.password) {
      setError('Please enter your username and password.');
      return;
    }

    setLoading(true);
    try {
      const res = await authAPI.login({
        username: form.username,
        password: form.password,
      });

      if (res.data.success) {
        const redirectPath = login(res.data.user, res.data.token);
        navigate(redirectPath, { replace: true });
      }
    } catch (err) {
      setError(
        err.response?.data?.message || 'Login failed. Check your credentials.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-root">

      {/* ── Left: Branding Panel ── */}
      <div className="login-brand">
        <div className="brand-logo">
          <div className="brand-logo-icon">🏥</div>
          <div className="brand-logo-text">EternalCare</div>
        </div>

        <h1 className="brand-tagline">
          Caring made<br />
          <span>smarter.</span>
        </h1>

        <p className="brand-sub">
          A complete home care management platform for
          agencies, caregivers, and case managers.
        </p>

        <div className="brand-features">
          {[
            'Real-time caregiver scheduling & GPS clock-in',
            'Medicaid authorization tracking',
            'Automated billing and payroll',
            'Secure client records management',
          ].map((f) => (
            <div className="brand-feature" key={f}>
              <div className="brand-feature-dot" />
              {f}
            </div>
          ))}
        </div>
      </div>

      {/* ── Right: Login Form ── */}
      <div className="login-form-panel">
        <div className="login-form-inner">
          <h2 className="login-welcome">Welcome back</h2>
          <p className="login-welcome-sub">Sign in to your EternalCare account</p>

          {error && (
            <div className="login-error">
              ⚠️ {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Username */}
            <div className="login-field">
              <label className="login-label">Username / Email</label>
              <div className="login-input-wrap">
                <span className="login-input-icon">👤</span>
                <input
                  className="login-input"
                  type="text"
                  name="username"
                  placeholder="admin@eternalcare.com"
                  value={form.username}
                  onChange={handleChange}
                  autoComplete="username"
                  autoFocus
                />
              </div>
            </div>

            {/* Password */}
            <div className="login-field">
              <label className="login-label">Password</label>
              <div className="login-input-wrap">
                <span className="login-input-icon">🔒</span>
                <input
                  className="login-input"
                  type={showPass ? 'text' : 'password'}
                  name="password"
                  placeholder="Enter your password"
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="login-eye-btn"
                  onClick={() => setShowPass(p => !p)}
                  tabIndex={-1}
                >
                  {showPass ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <button className="login-btn" type="submit" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In →'}
            </button>
          </form>

          {/* Dev hint */}
          <div className="login-hint">
            <strong>Admin:</strong> admin@eternalcare.com / Admin@123<br />
            <strong>Caregiver:</strong> sarah.johnson@eternalcare.com / Password@123<br />
            <strong>Client:</strong> mary.smith@client.com / Password@123
            
          </div>
        </div>
      </div>

    </div>
  );
}